import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare, Radio, Coins, Bot, Zap, BarChart3, Heart, ShieldCheck,
  FileText, Users, ArrowRight, Check, Handshake, Lock, Server, EyeOff,
} from "lucide-react";
import { SiGoogle, SiTwitch, SiYoutube, SiKick, SiStripe, SiRazorpay, SiMeta } from "react-icons/si";
import { FaAws } from "react-icons/fa";
import { Reveal, SectionHeading } from "@/components/motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/api";

const FEATURES = [
  { Icon: MessageSquare, title: "Unified Chat", desc: "Every platform's chat in one window — read, reply, and moderate without tab-hopping." },
  { Icon: Radio, title: "Live Stream Control", desc: "Start, title, and manage your streams across platforms from a single dashboard." },
  { Icon: Coins, title: "Tips & Earnings", desc: "Direct tipping with real-time alerts — 100% of every tip goes straight to you." },
  { Icon: Bot, title: "Destiny AI", desc: "An assistant that drafts replies, summarizes chat, and surfaces moments worth clipping." },
  { Icon: Zap, title: "Pulse Automations", desc: "Trigger shoutouts, overlays, and rewards automatically when tips and follows land." },
  { Icon: BarChart3, title: "Performance Analytics", desc: "Cross-platform growth, revenue, and retention metrics in one clean view." },
  { Icon: Heart, title: "Creator Loyalty", desc: "Recognize returning supporters with streaks, badges, and loyalty rewards." },
  { Icon: ShieldCheck, title: "Moderation Tools", desc: "AI-assisted filters, blocked terms, and trusted mod roles keep chat healthy." },
  { Icon: FileText, title: "Custom Tip Pages", desc: "A branded tipping page with your message, your style, and your payment account." },
  { Icon: Users, title: "Team Management", desc: "Bring editors, mods, and managers in with role-based access to your workspace." },
];

const ECOSYSTEM = [
  { name: "Google", Icon: SiGoogle }, { name: "Amazon Web Services", Icon: FaAws },
  { name: "Twitch", Icon: SiTwitch }, { name: "YouTube", Icon: SiYoutube },
  { name: "Kick", Icon: SiKick }, { name: "Stripe", Icon: SiStripe },
  { name: "Razorpay", Icon: SiRazorpay }, { name: "Meta", Icon: SiMeta },
];

const STEPS = [
  { n: "01", title: "Explore Destiny Tip", desc: "Browse the ecosystem, features, and creators already on the platform." },
  { n: "02", title: "Apply to join", desc: "Complete the six-step creator application — it takes about ten minutes." },
  { n: "03", title: "Complete verification", desc: "Our team reviews your channels and confirms ownership." },
  { n: "04", title: "Receive decision", desc: "You get an email and a status update at every stage of review." },
  { n: "05", title: "Follow onboarding", desc: "If approved, publish your profile and connect your platforms." },
];

const FAQS = [];

function useFaqs() {
  const [faqs, setFaqs] = useState([]);
  useEffect(() => {
    api.get("/faqs").then((r) => setFaqs((r.data.items || []).slice(0, 6))).catch(() => {});
  }, []);
  return faqs;
}

function PricingTier({ name, price, annualPrice, period, features, cta, highlight, note }) {
  return (
    <div className={`card-dt p-8 flex flex-col ${highlight ? "!border-dt-accent/60 relative shadow-[0_0_40px_-10px_rgba(141,185,232,0.25)]" : ""}`}
         data-testid={`pricing-tier-${name.toLowerCase()}`}>
      {highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.2em] uppercase font-bold bg-dt-accent text-dt-bg rounded-full px-3 py-1">
          Most popular
        </span>
      )}
      <h3 className="font-heading text-xl font-semibold text-dt-text">{name}</h3>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="font-heading text-4xl font-bold text-metallic">{price}</span>
        {period && <span className="text-dt-muted text-sm">/{period}</span>}
      </div>
      {annualPrice && <p className="mt-1 text-xs text-[#4ade80]">{annualPrice} billed annually — 2 months free</p>}
      {note && <p className="mt-1 text-xs text-dt-muted">{note}</p>}
      <ul className="mt-6 space-y-3 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-dt-muted">
            <Check className="w-4 h-4 text-dt-accent shrink-0 mt-0.5" /> {f}
          </li>
        ))}
      </ul>
      <Link to={name === "Partner" ? "/contact" : "/apply"}
        className={`mt-8 text-center rounded-full px-6 py-3 text-sm font-semibold transition-colors ${
          highlight ? "bg-dt-silver text-dt-bg hover:bg-white" : "bg-dt-bg2 text-dt-text border border-dt-border hover:border-dt-accent"}`}
        data-testid={`pricing-cta-${name.toLowerCase()}`}>
        {cta}
      </Link>
    </div>
  );
}

function LandingFaq() {
  const faqs = useFaqs();
  return (
    <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
      {faqs.map((f, i) => (
        <AccordionItem key={f.id} value={f.id} className="card-dt px-6 !rounded-xl border-dt-border">
          <AccordionTrigger className="text-left font-heading font-medium text-dt-text hover:no-underline py-5" data-testid={`faq-q-${i}`}>
            {f.question}
          </AccordionTrigger>
          <AccordionContent className="text-sm text-dt-muted leading-relaxed pb-5">{f.answer}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export default function LandingSections() {
  const [annual, setAnnual] = useState(false);

  return (
    <>
      {/* FEATURES */}
      <section id="features" className="py-24 sm:py-32" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Everything in one place" title="Ten tools. Zero tab chaos."
            sub="Destiny Tip brings the daily workflow of a streamer — chat, tips, moderation, analytics — into a single command center." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={(i % 3) * 0.1}>
                <div className="card-dt p-6 h-full" data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="w-11 h-11 rounded-xl bg-dt-accent/10 border border-dt-accent/20 flex items-center justify-center mb-4">
                    <f.Icon className="w-5 h-5 text-dt-accent" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-dt-text">{f.title}</h3>
                  <p className="mt-2 text-sm text-dt-muted leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ECOSYSTEM MARQUEE */}
      <section className="py-14 border-y border-dt-border bg-dt-bg2/40 overflow-hidden" data-testid="ecosystem-section">
        <p className="text-center text-xs tracking-[0.25em] uppercase font-bold text-dt-muted mb-8">Platform Ecosystem</p>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-dt-bg to-transparent z-10" aria-hidden="true" />
          <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-dt-bg to-transparent z-10" aria-hidden="true" />
          <div className="flex marquee-track w-max" data-testid="ecosystem-marquee">
            {[0, 1].map((dup) => (
              <div key={dup} className="flex shrink-0" aria-hidden={dup === 1}>
                {ECOSYSTEM.map((e) => (
                  <div key={`${dup}-${e.name}`} className="flex items-center gap-3 px-10 py-3">
                    <e.Icon className="w-6 h-6 text-dt-silver/70" />
                    <span className="text-sm font-semibold text-dt-silver/70 whitespace-nowrap">{e.name}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-24 sm:py-32" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="How it works" title="From application to approval, in five steps" />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-5 gap-5">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="card-dt p-6 h-full relative" data-testid={`step-${i + 1}`}>
                  <span className="font-mono2 text-dt-accent/50 text-sm">{s.n}</span>
                  <h3 className="mt-3 font-heading font-semibold text-dt-text">{s.title}</h3>
                  <p className="mt-2 text-sm text-dt-muted leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 sm:py-32 bg-dt-bg2/40" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading align="center" eyebrow="Pricing" title="Start free. Grow into Pro."
            sub="Every tier keeps 100% of tips flowing to the creator. Upgrade only when you need more platforms and deeper tools." />
          <div className="mt-10 flex items-center justify-center gap-3" data-testid="billing-toggle">
            <span className={`text-sm ${!annual ? "text-dt-text font-semibold" : "text-dt-muted"}`}>Monthly</span>
            <button
              onClick={() => setAnnual(!annual)}
              className="w-12 h-6 rounded-full bg-dt-card border border-dt-border relative transition-colors"
              role="switch" aria-checked={annual} aria-label="Toggle annual billing"
              data-testid="billing-toggle-switch"
            >
              <span className={`absolute top-0.5 w-4.5 h-4.5 w-5 h-5 rounded-full bg-dt-silver transition-transform duration-300 ${annual ? "translate-x-6" : "translate-x-0.5"}`} />
            </button>
            <span className={`text-sm ${annual ? "text-dt-text font-semibold" : "text-dt-muted"}`}>
              Annual <span className="text-[#4ade80] text-xs ml-1">save 2 months</span>
            </span>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Reveal delay={0}>
              <PricingTier name="Starter" price={annual ? "₹83" : "₹99"} period="mo"
                annualPrice={annual ? "₹990/yr" : null}
                features={["Basic streaming tools", "1 platform connection", "Standard tip page", "Email support"]}
                cta="Get Starter" />
            </Reveal>
            <Reveal delay={0.1}>
              <PricingTier name="Pro" price={annual ? "₹499" : "₹599"} period="mo" highlight
                annualPrice={annual ? "₹5,990/yr" : null}
                features={["All platforms connected", "Destiny AI moderation", "Full performance analytics", "Custom tip page", "Pulse automations", "Priority support"]}
                cta="Get Pro" />
            </Reveal>
            <Reveal delay={0.2}>
              <PricingTier name="Partner" price="Invite only"
                features={["Featured placement in directory", "Verified Destiny Tip badge", "Dedicated success manager", "Early access to new tools"]}
                cta="Apply" note="Extended to established creators by invitation." />
            </Reveal>
          </div>
          <Reveal className="text-center mt-8">
            <Link to="/pricing" className="text-sm text-dt-accent hover:underline" data-testid="pricing-full-link">
              Full comparison & checkout →
            </Link>
          </Reveal>
        </div>
      </section>

      {/* RECRUITMENT CTA */}
      <section className="py-24 sm:py-32 relative overflow-hidden" data-testid="recruitment-section">
        <div className="absolute inset-0" aria-hidden="true">
          <div className="absolute bottom-[-30%] left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-dt-accent/10 blur-[130px]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold text-metallic leading-tight">
              Your audience is waiting. Let us build something together.
            </h2>
            <p className="mt-5 text-dt-muted text-base sm:text-lg max-w-xl mx-auto">
              Complete the application, submit your details, wait for review, receive a decision.
              Every step is tracked on your personal status page.
            </p>
            <Link to="/apply"
              className="inline-block mt-9 glow-pulse bg-dt-silver text-dt-bg font-semibold rounded-full px-9 py-4 text-base transition-transform duration-200 hover:scale-105 hover:bg-white"
              data-testid="recruitment-apply-btn">
              Apply to Become a Creator
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PARTNER PROGRAM */}
      <section id="partners" className="py-24 sm:py-32 bg-dt-bg2/40" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <SectionHeading eyebrow="Partner program" title="For creators ready to lead"
            sub="Partner status is our invite-only tier for established creators: a verified badge, featured placement across the directory, and a dedicated success manager. Partners are hand-picked from the applicant pool — applying is the first step." />
          <div className="grid gap-4">
            {[
              { Icon: Handshake, t: "Featured placement", d: "Your channel highlighted across the directory and homepage live strip." },
              { Icon: ShieldCheck, t: "Verified badge", d: "A Destiny Tip verification mark your community can trust." },
              { Icon: Users, t: "Dedicated support", d: "A named success manager and faster review on everything." },
            ].map((x, i) => (
              <Reveal key={x.t} delay={i * 0.1}>
                <div className="card-dt p-5 flex gap-4">
                  <x.Icon className="w-6 h-6 text-dt-accent shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-dt-text">{x.t}</h3>
                    <p className="text-sm text-dt-muted mt-1">{x.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECURITY */}
      <section id="security" className="py-24 sm:py-32" data-testid="security-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Security & privacy" title="Built to be trusted"
            sub="Applications are reviewed by humans, protected by role-based access, and every sensitive action is audit-logged." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { Icon: Lock, t: "Private by default", d: "Application data is visible only to you and the review team — never to other applicants or public analytics." },
              { Icon: Server, t: "Hardened access", d: "Role-based dashboard access, rate-limited public endpoints, and expiring sessions." },
              { Icon: EyeOff, t: "Your data, your call", d: "Request a copy or deletion of your data at any time from our data deletion page." },
            ].map((x, i) => (
              <Reveal key={x.t} delay={i * 0.1}>
                <div className="card-dt p-6 h-full">
                  <x.Icon className="w-6 h-6 text-dt-accent mb-4" />
                  <h3 className="font-heading font-semibold text-dt-text">{x.t}</h3>
                  <p className="text-sm text-dt-muted mt-2 leading-relaxed">{x.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 sm:py-32 bg-dt-bg2/40" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading align="center" eyebrow="FAQ" title="Questions, answered" />
          <Reveal className="mt-12">
            <LandingFaq />
            <div className="text-center mt-6">
              <Link to="/faq" className="text-sm text-dt-accent hover:underline" data-testid="faq-all-link">View all questions →</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* RESOURCES */}
      <section id="resources" className="py-24 sm:py-32" data-testid="resources-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Blog & resources" title="Learn the ecosystem" />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { t: "Creator Application Policy", d: "Exactly what we review, what we ask for, and how decisions are made.", href: "/application-policy", tag: "Policy" },
              { t: "Security & Data Practices", d: "How applicant data is stored, who can see it, and how to request deletion.", href: "/privacy", tag: "Privacy" },
              { t: "Platform Status", d: "Live operational status of the Destiny Tip ecosystem and integrations.", href: "/status", tag: "Status" },
            ].map((r, i) => (
              <Reveal key={r.t} delay={i * 0.1}>
                <Link to={r.href} className="card-dt p-6 block h-full group" data-testid={`resource-card-${i}`}>
                  <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-dt-accent">{r.tag}</span>
                  <h3 className="mt-3 font-heading text-lg font-semibold text-dt-text group-hover:text-dt-accent2 transition-colors">{r.t}</h3>
                  <p className="mt-2 text-sm text-dt-muted leading-relaxed">{r.d}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm text-dt-accent">Read <ArrowRight className="w-4 h-4" /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
